import { z } from 'zod';
import { telemetryFormatSchema } from '../enums.js';
/**
 * Un échantillon de télémétrie, normalisé quel que soit le constructeur d'origine (FR-16).
 *
 * `timestampMs` est un décalage **relatif au début de la vidéo**, pas un horodatage absolu :
 * c'est ce qui permet de synchroniser la carte avec la lecture sans dépendre de l'horloge du
 * drone, souvent mal réglée.
 */
export const telemetrySampleSchema = z.object({
    timestampMs: z.number().int().nonnegative(),
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    /** Altitude relative au point de décollage, en mètres. */
    altitudeM: z.number().nullable(),
    /** Altitude absolue au-dessus du niveau de la mer, en mètres. */
    absoluteAltitudeM: z.number().nullable(),
    /** Cap en degrés, 0 = nord, sens horaire. */
    headingDeg: z.number().min(0).max(360).nullable(),
    pitchDeg: z.number().min(-180).max(180).nullable(),
    rollDeg: z.number().min(-180).max(180).nullable(),
    /** Vitesse sol en mètres par seconde. */
    speedMps: z.number().nonnegative().nullable(),
});
/** Résultat d'un parseur, prêt à être persisté. */
export const parsedTelemetrySchema = z.object({
    format: telemetryFormatSchema,
    sampleCount: z.number().int().nonnegative(),
    samples: z.array(telemetrySampleSchema),
    /** Horodatage absolu du premier échantillon, si le fichier le porte. */
    startedAt: z.coerce.date().nullable(),
    /** Anomalies non bloquantes : lignes ignorées, champs manquants. */
    warnings: z.array(z.string()).default([]),
});
/** Trace de vol renvoyée au front pour l'affichage cartographique (FR-24). */
export const flightTrackSchema = z.object({
    videoId: z.string(),
    format: telemetryFormatSchema,
    sampleCount: z.number().int().nonnegative(),
    /** Emprise de la trace : [ouest, sud, est, nord]. */
    bounds: z.tuple([z.number(), z.number(), z.number(), z.number()]).nullable(),
    /** Distance parcourue au sol, en mètres. */
    distanceM: z.number().nonnegative().nullable(),
    maxAltitudeM: z.number().nullable(),
    /**
     * Échantillons éventuellement sous-échantillonnés pour l'affichage : au-delà de quelques
     * milliers de points, MapLibre ralentit sans que l'utilisateur y gagne en précision.
     */
    samples: z.array(telemetrySampleSchema),
});
export const uploadTelemetrySchema = z.object({
    videoId: z.string().cuid2(),
    filename: z.string().trim().min(1).max(255),
    format: telemetryFormatSchema.optional(),
});
/**
 * Correspondance de colonnes pour le parseur CSV générique. Les exports varient tellement
 * d'un outil à l'autre qu'imposer un format unique reviendrait à refuser la moitié des fichiers.
 */
export const csvColumnMappingSchema = z.object({
    timestamp: z.string(),
    latitude: z.string(),
    longitude: z.string(),
    altitude: z.string().optional(),
    heading: z.string().optional(),
    pitch: z.string().optional(),
    roll: z.string().optional(),
    speed: z.string().optional(),
});
//# sourceMappingURL=telemetry.js.map